import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import logo from "@/assets/aude-logo.png";
import { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import SearchCommand from "@/components/SearchCommand";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const Navigation = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [publishersOpen, setPublishersOpen] = useState(false);
  
  const { data: publishers } = useQuery({
    queryKey: ["publishers-nav"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("publishers")
        .select("id, name, slug, country")
        .or("archived.is.null,archived.eq.false")
        .order("name")
        .limit(10);
      
      if (error) throw error;
      return data;
    },
  });

  const links = [
    { href: "/", label: "Home" },
    { href: "/books", label: "Books" },
    { href: "/publishers", label: "Publishers", hasDropdown: true },
    { href: "/about", label: "About us" },
    { href: "/contact", label: "Contact" },
  ];

  return (
    <nav className="bg-card border-b border-border/50">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img src={logo} alt="Aude Rights Agency" width={171} height={32} className="h-8 lg:h-11 w-auto" />
          </Link>

          {/* Mobile Search */}
          <div className="lg:hidden flex items-center gap-1 ml-auto mr-2">
            <SearchCommand isMobile />
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center flex-1 gap-10">
            {links.map((link) => (
              link.hasDropdown ? (
                <div 
                  key={link.href}
                  className="relative"
                  onMouseEnter={() => setPublishersOpen(true)}
                  onMouseLeave={() => setPublishersOpen(false)}
                >
                  <Link
                    to={link.href}
                    className={cn(
                      "font-noto text-sm font-medium tracking-wide transition-colors hover:text-primary inline-flex items-center gap-1",
                      location.pathname.startsWith("/publishers")
                        ? "text-primary"
                        : "text-foreground"
                    )}
                  >
                    {link.label}
                    <ChevronDown className={cn(
                      "w-4 h-4 transition-transform duration-200",
                      publishersOpen && "rotate-180"
                    )} />
                  </Link>
                  
                  {/* Dropdown */}
                  {publishersOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
                      <div className="bg-card border border-border rounded-lg shadow-elevated py-2 min-w-[240px]">
                        {publishers?.slice(0, 5).map((publisher) => {
                          const logoUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/logo/${publisher.slug}_logo.png`;
                          return (
                            <Link
                              key={publisher.id}
                              to={`/publishers/${publisher.slug}`}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                            >
                              <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                                <img
                                  src={logoUrl}
                                  alt=""
                                  className="w-full h-full object-contain p-1"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              </div>
                              <span className="font-medium truncate">{publisher.name}</span>
                            </Link>
                          );
                        })}
                        <div className="border-t border-border mt-2 pt-2 px-4 pb-1">
                          <Link
                            to="/publishers"
                            className="text-sm font-medium text-primary hover:underline"
                          >
                            See all publishers →
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    "font-noto text-sm font-medium tracking-wide transition-colors hover:text-primary",
                    location.pathname === link.href
                      ? "text-primary"
                      : "text-foreground"
                  )}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* Desktop Search */}
          <div className="hidden lg:flex items-center gap-2">
            <SearchCommand />
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border/50">
            <div className="flex flex-col gap-1">
              {links.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "px-4 py-3 rounded-lg text-sm font-medium tracking-wide transition-colors",
                    location.pathname === link.href
                      ? "text-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Dotted border separator */}
      <div 
        className="h-[4px]" 
        style={{
          backgroundImage: `radial-gradient(circle, hsl(var(--primary)) 2.5px, transparent 2.5px)`,
          backgroundSize: '16px 4px',
          backgroundRepeat: 'repeat-x',
          backgroundPosition: 'center'
        }}
      />
    </nav>
  );
};

export default Navigation;
